import { Dictionary } from "lodash";
import { ResourceCategory } from "./inventoryResource.model";
import {
    AggregationCategorySumDetails,
    AggregationCategorySumWithExtraInfo,
    PointInTime,
    RawAggregationCategorySumDetails,
} from "./dashboards.model";

export type AggregationDict = Dictionary<number>;

export type ViolationsCycleTime = Record<string, Record<number, number>>;

export type RulesAmounts = Record<string, Array<string>>;

export type OpenViolations = Record<string, number>;

export type PluginTypes = Record<string, number>;

export type OwaspRisksForAi = Record<string, number>;

export interface AggregationCategorySum {
    categoryName: string;
    categorySum: AggregationDict;
}

// TODO: Remove once we remove 'data' from AggregationDataCategoryAmount on the backend
export type RawAggregationCategorySum = Pick<AggregationCategorySum, "categoryName"> & { amountPerSubCategory: { data: AggregationDict } };

export interface AggregationCountByTime {
    creationTime: Date;
    amount: number;
}

export type AggregationsTrend = Record<string, AggregationCountByTime[]>;

export const toAggregationTrend = (item: AggregationCountByTime) => ({ x: item.creationTime, y: item.amount }) as PointInTime;

export interface AggregationRulesSum {
    totalRules: string[];
    failedRules: string[];
}

// Deprecated field, keeping it for future use and avoiding from breaking the code logic
export type AggregationsDataSecurityScoreComponents = [AggregationDict, RuleSecurityScoreComponentsDict | null];

export type RuleSecurityScoreComponentsDict = {
    [ruleId: string]: {
        [securityScoreComponents in SecurityScoreComponents]: number;
    };
};

export enum SecurityScoreComponents {
    severityScoreWeight = "severityScoreWeight",
    resourcesDetected = "resourcesDetected",
    supportedResourceType = "supportedResourceType",
}

export const aggregatedKeysSeparator = "__";

export enum AggregationType {
    // Visibility dashboard aggregations
    totalResourcesOfCategory = "totalResourcesOfCategory",
    topBuilders = "topBuilders",
    buildersAdoption = "buildersAdoption",
    unusedResources = "unusedResources",
    orphanResources = "orphanResources",

    // Common aggregations - not being used as part of a specific dashboard
    violationsRuleAmountPerPolicy = "violationsRuleAmountPerPolicy",
    resourcesCreatedPerDepartment = "resourcesCreatedPerDepartment",
}

export enum ChartsAggregationType {
    // Main
    owaspBySeverities = "owaspBySeverities",
    owaspLlmBySeverities = "owaspLlmBySeverities",
    mitreBySeverities = "mitreBySeverities",
    openViolationsSeverityDistribution = "openViolationsSeverityDistribution",
    rulesAmounts = "rulesAmounts",
    lowCodeNoCodeAdoption = "lowCodeNoCodeAdoption",
    riskMappingScore = "riskMappingScore",

    // Risk
    violationsOvertime = "violationsOvertime",
    topRiskyEnvironments = "topRiskyEnvironments",
    topRiskyUsers = "topRiskyUsers",
    violationsCycleTime = "violationsCycleTime",

    // AI
    copilotsAdoption = "copilotsAdoption",
    owaspRiskDistributionForAi = "owaspRiskDistributionForAi",
    openViolationsSeverityDistributionForAi = "openViolationsSeverityDistributionForAi",
    m365copilotPluginTypesDistribution = "m365copilotPluginTypesDistribution", // M365Copilot specific
    m365copilotTopPlugins = "m365copilotTopPlugins", // M365Copilot specific
}

type AggregationsDataType =
    | AggregationDict
    | RawAggregationCategorySumDetails[]
    | AggregationCategorySum[]
    | AggregationsTrend
    | AggregationRulesSum
    | AggregationsDataSecurityScoreComponents
    | ViolationsCycleTime
    | RulesAmounts;

export enum AggregationsType {
    common = "common", // Represents all the aggregations which don't belong to a certain dashboard
    main = "main",
    visibility = "visibility",
    enterpriseAi = "enterpriseAi",
}

export class AggregationsModel {
    public readonly zenityAccountId: string;
    public readonly aggregationsData: {
        [aggregationType in keyof typeof AggregationType | keyof typeof ChartsAggregationType]: {
            data: AggregationsDataType;
        };
    };
}

const asType = <D extends AggregationsDataType>(model: AggregationsModel, type: keyof typeof AggregationType) =>
    model.aggregationsData[type].data as D;

export const unusedResourcesData = (model: AggregationsModel) => asType<AggregationDict>(model, AggregationType.unusedResources);

export const orphanResourcesData = (model: AggregationsModel) => asType<AggregationDict>(model, AggregationType.orphanResources);

export const buildersAdoptionData = (model: AggregationsModel) => asType<AggregationsTrend>(model, AggregationType.buildersAdoption);

export const totalResourcesData = (model: AggregationsModel) => asType<AggregationDict>(model, AggregationType.totalResourcesOfCategory);

export const topBuilders = (model: AggregationsModel) => asType<RawAggregationCategorySumDetails[]>(model, AggregationType.topBuilders);

export const ResourceCategories = [
    ResourceCategory.automation,
    ResourceCategory.connection,
    ResourceCategory.application,
    ResourceCategory.dataStorage,
    ResourceCategory.environment,
];

export const BuilderCategories = [ResourceCategory.automation, ResourceCategory.application, ResourceCategory.dataStorage];

export const toTopBuilder = (topBuilder: RawAggregationCategorySumDetails) => {
    const [builderId, builderDisplayName] = topBuilder.categoryName.split(aggregatedKeysSeparator);
    return {
        categoryName: builderDisplayName,
        categoryLinkValue: builderId,
        amountPerSubCategory: topBuilder.amountPerSubCategory.data,
    } as AggregationCategorySumDetails;
};

export const toAggregationCategorySumWithExtraInfo = (entry: RawAggregationCategorySumDetails) => {
    const [resourceId, resourceDisplayName, resourceType] = entry.categoryName.split(aggregatedKeysSeparator);
    return {
        categoryName: resourceType,
        displayName: resourceDisplayName,
        categoryLinkValue: resourceId,
        amountPerSubCategory: entry.amountPerSubCategory.data,
    } as AggregationCategorySumWithExtraInfo;
};
